import { Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "../auth/SessionContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { ApiError } from "../lib/api";
import type { SessionRole } from "../types";
import { Button, Field, InlineNotice } from "../components/ui";
import { ThemeToggle } from "../components/ThemeToggle";

export function LoginPage() {
  const { authenticated, checking, login } = useSession();
  const location = useLocation();
  const [role, setRole] = useState<SessionRole>("operator");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle("Ingresar");

  if (checking) return <div className="boot-screen"><span className="brand__mark"><ShieldCheck size={25} /></span><span>Cargando Centro Monitoreo Hikdos…</span></div>;
  if (authenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password) {
      setError("Ingresá la contraseña para continuar.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await login(password, role);
    } catch (nextError) {
      setError(nextError instanceof ApiError && nextError.status === 401
        ? "La contraseña no corresponde al tipo de acceso seleccionado."
        : nextError instanceof Error ? nextError.message : "No se pudo iniciar sesión.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-story" aria-label="Presentación">
        <div className="login-story__brand"><span className="brand__mark"><ShieldCheck size={23} /></span><strong>Centro Monitoreo Hikdos</strong></div>
        <div className="login-story__content">
          <p className="eyebrow">Centro de monitoreo</p>
          <h1>Lo importante,<br />a simple vista.</h1>
          <p>Conectividad y grabadores de todos los edificios en una única vista clara.</p>
          <div className="login-story__signal" aria-hidden="true">
            <span className="signal-orbit signal-orbit--one" />
            <span className="signal-orbit signal-orbit--two" />
            <span className="signal-core"><ShieldCheck size={30} /></span>
          </div>
        </div>
        <p className="login-story__foot">Monitoreo operativo · Acceso protegido</p>
      </section>
      <section className="login-panel">
        <div className="login-theme-toggle"><ThemeToggle /></div>
        <form className="login-card" onSubmit={handleSubmit}>
          <div className="login-card__icon"><LockKeyhole size={24} aria-hidden="true" /></div>
          <p className="eyebrow">Acceso seguro</p>
          <h2>Ingresar al monitoreo</h2>
          <p className="login-card__intro">Elegí el tipo de acceso e ingresá la contraseña compartida.</p>
          <fieldset className="role-selector">
            <legend className="sr-only">Tipo de acceso</legend>
            <label className={role === "operator" ? "selected" : ""}>
              <input type="radio" name="role" value="operator" checked={role === "operator"} onChange={() => setRole("operator")} />
              <span><strong>Operador</strong><small>Monitorear y atender alertas</small></span>
            </label>
            <label className={role === "admin" ? "selected" : ""}>
              <input type="radio" name="role" value="admin" checked={role === "admin"} onChange={() => setRole("admin")} />
              <span><strong>Administrador</strong><small>Configurar conexiones y avisos</small></span>
            </label>
          </fieldset>
          <Field label="Contraseña">
            <span className="password-field">
              <input autoFocus autoComplete="current-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} aria-describedby={error ? "login-error" : undefined} />
              <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </Field>
          {error ? <div id="login-error"><InlineNotice tone="danger" title="No pudimos ingresar">{error}</InlineNotice></div> : null}
          <Button type="submit" loading={submitting} className="button--full">Ingresar</Button>
          <p className="login-help">Si no conocés la contraseña, pedísela a la persona responsable del sistema.</p>
        </form>
      </section>
    </main>
  );
}
